import cn from 'classnames';
import { useRouter } from 'next/router';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { Row } from 'components/layout';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { PAGE_ROUTES } from 'lib/page-routes';

import { useAuth } from 'lib/providers/AuthProvider';
import SocietyNewslettersTable from '../SocietyNewslettersTable';

import styles from './SocietyNewslettersListing.module.scss';

export const SocietyNewslettersListing: React.FC = memo(() => {
  const router = useRouter();
  const { isManager, isAuthLoading } = useAuth();

  const scrollTabRef = useRef<HTMLDivElement | null>(null);

  const headerRef = useRef<HTMLDivElement | null>(null);
  const [headerScrolled, setHeaderScrolled] = useState(false);

  useEffect(() => {
    if (isAuthLoading) return;
    if (!isManager) {
      router.replace(PAGE_ROUTES.SIGN_IN_SOCIETY);
    }
  }, [isManager, isAuthLoading]);

  const onHeaderShow = useCallback(() => {
    setHeaderScrolled(false);
  }, []);

  const onHeaderHide = useCallback(() => {
    setHeaderScrolled(true);
  }, []);

  useVisibleOnIntersect({
    elRef: headerRef,
    threshold: 1,
    onIntersect: onHeaderShow,
    onHidden: onHeaderHide,
    rootMargin: '0px',
    isEqualToThreshold: true,
  });

  useEffect(() => {
    return () => {
      scrollTabRef.current = null;
    };
  }, []);

  const [countMapping, setCountMapping] = useState<Record<string, number>>({});
  return (
    <section
      className={styles.SocietyNewslettersListing}
      data-testid="SocietyNewslettersListing"
    >
      {headerScrolled ? null : (
        <Row
          align="center"
          columnGap={4}
          className={styles.redirect}
          onClick={() => router.push(PAGE_ROUTES.DASHBOARD)}
        >
          <Icon iconName="chevron-left" size="large" />
          Dashboard
        </Row>
      )}
      <div
        ref={headerRef}
        className={cn({
          [styles.header]: true,
          [styles.headerScrolled]: headerScrolled,
        })}
      >
        <p className={styles.title}>Newsletters</p>
        <ButtonV3
          color={Object.keys(countMapping).length > 0 ? 'primary' : 'secondary'}
          onClick={() => router.push(PAGE_ROUTES.NEWSLETTER_CREATE)}
          className={styles.whiteButton}
        >
          Create new
        </ButtonV3>
      </div>

      <div className={styles.container}>
        <>
          <div ref={scrollTabRef}>
            <SocietyNewslettersTable setCountMapping={setCountMapping} />
          </div>
        </>
      </div>
    </section>
  );
});

SocietyNewslettersListing.displayName = 'SocietyNewslettersListing';
